
import { DataScienceCourse } from './types';

export const DATASCIENCE_ADVANCED_COURSE: DataScienceCourse = {
  id: 'datascience-advanced',
  title: 'Advanced Data Science & MLOps',
  description: 'Master production-grade data science with MLOps, big data architecture, advanced analytics, and leadership skills for senior roles.',
  level: 'advanced',
  totalDuration: '18-22 hours',
  image: '/api/placeholder/400/300',
  prerequisites: [
    'Completed Intermediate Data Science course',
    'Solid understanding of machine learning',
    'Experience with Python and SQL',
    'Familiarity with cloud platforms'
  ],
  learningOutcomes: [
    'Implement MLOps best practices and CI/CD for ML',
    'Design scalable big data architectures',
    'Apply causal inference and experimental design',
    'Lead data science teams and strategy',
    'Build domain-specific ML solutions',
    'Implement data governance and quality frameworks',
    'Deploy models at enterprise scale',
    'Drive business value through data science initiatives'
  ],
  modules: [
    {
      id: 'ds-mlops',
      title: 'MLOps & Production Deployment',
      description: 'Learn to deploy, monitor, and maintain machine learning models in production environments with DevOps best practices.',
      duration: '3 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement CI/CD pipelines for ML models',
        'Set up model monitoring and alerting',
        'Handle model versioning and rollbacks',
        'Scale ML systems for production workloads'
      ],
      content: `# MLOps & Production Deployment

MLOps (Machine Learning Operations) is the practice of applying DevOps principles to machine learning systems, ensuring reliable, scalable, and maintainable ML solutions.

## MLOps Principles

### Automation
- Automated model training and validation
- Continuous integration and deployment
- Automated testing and quality assurance
- Infrastructure as code

### Monitoring
- Model performance monitoring
- Data drift detection
- Infrastructure monitoring
- Business metrics tracking

### Reproducibility
- Version control for code, data, and models
- Environment management
- Experiment tracking
- Artifact management

### Collaboration
- Cross-functional team integration
- Documentation and knowledge sharing
- Standardized processes
- Communication protocols

## CI/CD for Machine Learning

### Continuous Integration
- Automated testing of data pipelines
- Model validation and quality checks
- Code quality and security scanning
- Integration testing

### Continuous Deployment
- Automated model deployment
- Canary releases and A/B testing
- Blue-green deployments
- Rollback strategies

### Pipeline Orchestration
- Workflow management
- Dependency handling
- Error handling and retries
- Resource optimization

## Model Monitoring in Production

### Performance Monitoring
- **Accuracy Metrics**: Track model performance over time
- **Latency Monitoring**: Response time and throughput
- **Resource Usage**: CPU, memory, and storage utilization
- **Error Rates**: Failed predictions and system errors

### Data Drift Detection
- **Statistical Tests**: KS test, PSI, Jensen-Shannon divergence
- **Distribution Monitoring**: Feature distribution changes
- **Concept Drift**: Changes in target variable relationships
- **Automated Alerts**: Threshold-based notifications

### Business Impact Monitoring
- **Revenue Impact**: Direct business metrics
- **User Experience**: Customer satisfaction scores
- **Operational Efficiency**: Process improvements
- **Cost Optimization**: Resource usage and savings

## Model Versioning and Management

### Model Registry
- Centralized model storage
- Version tracking and lineage
- Metadata management
- Access control and governance

### Experiment Tracking
- Hyperparameter logging
- Metric comparison
- Artifact storage
- Reproducibility metadata

### A/B Testing for Models
- Traffic splitting strategies
- Statistical significance testing
- Gradual rollout procedures
- Performance comparison frameworks`,
      codeExamples: [
        {
          title: 'MLOps Pipeline with Model Monitoring',
          language: 'python',
          code: `import joblib
import pandas as pd
import numpy as np
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import train_test_split
from sklearn.metrics import classification_report, roc_auc_score
import logging
from datetime import datetime
import json

class ModelMonitor:
    def __init__(self, model_name, threshold=0.05):
        self.model_name = model_name
        self.threshold = threshold
        self.baseline_stats = None
        self.alerts = []
        
    def set_baseline(self, X_train):
        """Set baseline statistics for drift detection"""
        self.baseline_stats = {
            'mean': X_train.mean().to_dict(),
            'std': X_train.std().to_dict(),
            'quantiles': X_train.quantile([0.25, 0.5, 0.75]).to_dict()
        }
        
    def detect_drift(self, X_new):
        """Detect data drift using statistical tests"""
        if self.baseline_stats is None:
            raise ValueError("Baseline statistics not set. Call set_baseline() first.")
            
        drift_detected = False
        drift_features = []
        
        current_stats = {
            'mean': X_new.mean().to_dict(),
            'std': X_new.std().to_dict()
        }
        
        # Check for significant changes in mean
        for feature in X_new.columns:
            baseline_mean = self.baseline_stats['mean'][feature]
            current_mean = current_stats['mean'][feature]
            
            # Simple threshold-based drift detection
            relative_change = abs(current_mean - baseline_mean) / abs(baseline_mean)
            
            if relative_change > self.threshold:
                drift_detected = True
                drift_features.append(feature)
                
        return drift_detected, drift_features
    
    def log_prediction(self, model_version, features, prediction, confidence, timestamp=None):
        """Log prediction for monitoring"""
        if timestamp is None:
            timestamp = datetime.now().isoformat()
            
        log_entry = {
            'timestamp': timestamp,
            'model_version': model_version,
            'prediction': prediction,
            'confidence': confidence,
            'features': features.to_dict() if hasattr(features, 'to_dict') else features
        }
        
        # In production, this would go to a proper logging system
        logging.info(f"Prediction logged: {json.dumps(log_entry)}")
        return log_entry

class ProductionModel:
    def __init__(self, model_path, model_version):
        self.model = joblib.load(model_path)
        self.version = model_version
        self.monitor = ModelMonitor(f"model_v{model_version}")
        
    def predict(self, X):
        """Make predictions with monitoring"""
        predictions = self.model.predict(X)
        probabilities = self.model.predict_proba(X)
        
        # Log predictions for monitoring
        for i, (pred, prob) in enumerate(zip(predictions, probabilities)):
            confidence = max(prob)
            self.monitor.log_prediction(
                model_version=self.version,
                features=X.iloc[i] if hasattr(X, 'iloc') else X[i],
                prediction=pred,
                confidence=confidence
            )
            
        return predictions, probabilities
    
    def health_check(self, X_sample):
        """Perform health check including drift detection"""
        try:
            # Check if model can make predictions
            predictions, _ = self.predict(X_sample)
            
            # Check for data drift
            drift_detected, drift_features = self.monitor.detect_drift(X_sample)
            
            health_status = {
                'status': 'healthy' if not drift_detected else 'warning',
                'drift_detected': drift_detected,
                'drift_features': drift_features,
                'prediction_success': True,
                'timestamp': datetime.now().isoformat()
            }
            
            if drift_detected:
                logging.warning(f"Data drift detected in features: {drift_features}")
                
            return health_status
            
        except Exception as e:
            logging.error(f"Model health check failed: {str(e)}")
            return {
                'status': 'unhealthy',
                'error': str(e),
                'prediction_success': False,
                'timestamp': datetime.now().isoformat()
            }

# Example usage
if __name__ == "__main__":
    # Configure logging
    logging.basicConfig(level=logging.INFO)
    
    # Generate sample data
    from sklearn.datasets import make_classification
    X, y = make_classification(n_samples=1000, n_features=10, random_state=42)
    X = pd.DataFrame(X, columns=[f'feature_{i}' for i in range(10)])
    
    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)
    
    # Train and save model
    model = RandomForestClassifier(random_state=42)
    model.fit(X_train, y_train)
    model_path = 'production_model_v1.pkl'
    joblib.dump(model, model_path)
    
    # Create production model instance
    prod_model = ProductionModel(model_path, version="1.0")
    prod_model.monitor.set_baseline(X_train)
    
    # Test predictions
    predictions, probabilities = prod_model.predict(X_test[:5])
    print(f"Predictions: {predictions}")
    print(f"Confidence scores: {probabilities.max(axis=1)}")
    
    # Perform health check
    health_status = prod_model.health_check(X_test[:100])
    print(f"Health Status: {health_status}")
    
    # Simulate data drift by shifting feature distributions
    X_drift = X_test.copy()
    X_drift['feature_0'] += 2  # Introduce drift in feature_0
    
    health_status_drift = prod_model.health_check(X_drift[:100])
    print(f"Health Status with Drift: {health_status_drift}")`,
          explanation: 'This example demonstrates a production-ready ML system with monitoring, drift detection, and health checks for reliable model deployment.'
        }
      ]
    }
  ]
};
